import Immutable from 'immutable';
import webapi, {REPORTED_EXCEPTION} from '../../webapi';
import { createModuleState } from '../moduleregistry';
import RenderContext from '../../components/definitions/rendercontext';
import Binder from '../../components/definitions/binder';
import { validateAll } from '../../components/definitions/misc/definitionutils';
import { processModifications } from '../../data/modificationutils';
import { updateEntityTimestamp } from '../../data/entitytimestamps';
import { getTabularInputIndexPathBase } from '../../components/definitions/forms/tabularinputdefinition';
import { setProperty } from '../../data/utils';
import { prependOnceListener } from 'superagent';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = createModuleState('EntityDetail');
const EMPTY_LIST = Immutable.List([]);

export const errorPath = ['tmp', 'errors'];
export const errorMessageListPath = ['tmp', 'errorMessages'];
export const readOnlyPath = ['tmp', 'readOnly'];
const originalErrorPath = ['tmp', 'originalErrors'];

let moduleKeySequence = 1;

function getErrorEntryKey(ee) {
	return ee.get('path').join('$') + '$' + ee.getIn(['error', 'code']);
}

export default class EntityDetailModel {
	#data;

	constructor(data = null) {
		this.#data = data || EMPTY_MAP;
	}

	setLifeCycleListener(func) {
		return new EntityDetailModel(this.#data.set('lifeCycleListener', func));
	}

	setLoadUri(uri) {
		return new EntityDetailModel(this.#data.set('loadUri', uri));
	}

	setSaveUri(uri) {
		return new EntityDetailModel(this.#data.set('saveUri', uri));
	}

	setHeader(header) {
		return new EntityDetailModel(this.#data.set('header', header));
	}

	setEntityName(entityName) {
		return new EntityDetailModel(this.#data.set('entityName', entityName));
	}

	getEntityName() {
		return this.#data.get('entityName');
	}

	getData() {
		return this.#data;
	}

	setProcessForSave(value) {
		return new EntityDetailModel(this.#data.set('processForSave', value));
	}

	setProcessOnLoad(func) {
		return new EntityDetailModel(this.#data.set('processOnLoad', func));
	}

	setOnNewEntity(func) {
		return new EntityDetailModel(this.#data.set('onNewEntity', func));
	}

	async onNewEntity() {
		const onNewEntity = this.#data.get('onNewEntity');
		if (typeof onNewEntity !== 'function') {
			return this;
		}
		return onNewEntity(this) || this;
	}

	setEmptyDetail() {
		return new EntityDetailModel(
			this.#data.set('detail', EMPTY_MAP).set('originalDetail', EMPTY_MAP).delete('tmp')
		);
	}

	async loadDetail(id, opts) {
		opts = opts || {};
		const prevId = this.#data.getIn(['detail', 'id']);
		let res = await webapi.execute(this.#data.get('loadUri'), {id});
		if (res === REPORTED_EXCEPTION) {
			return this;
		}
		let detail = Immutable.fromJS(res);
		const processOnLoad = this.#data.get('processOnLoad');
		if (processOnLoad) {
			detail = processOnLoad(detail);
		}
		let model = new EntityDetailModel(this.#data.set('detail', detail).set('originalDetail', detail).delete('tmp')).setMessage(opts._message);
		if (id !== null) {
			model = await model.validateAll({
				storeOriginalErrors: true
			});
		}
		const createReadOnlyMap = model.#data.get('createReadOnlyMap');
		if (createReadOnlyMap) {
			model.#data = model.#data.setIn(readOnlyPath.concat('detail'), createReadOnlyMap(detail));
		}
		return model.resetVolatile();
	}

	setModalModule(model) {
		return new EntityDetailModel(this.#data.set('modalModule', model.getState()));
	}

	onModalModuleClosed(func) {
		return new EntityDetailModel(this.#data.set('onModalModuleClosed', func));
	}

	deleteModalModule() {
		return new EntityDetailModel(this.#data.delete('modalModule'));
	}

	setReadOnly(value) {
		return new EntityDetailModel(this.#data.set('readOnly', value));
	}

	async reload() {
		const id = this.#data.getIn(['detail', 'id']);
		if (!id) {
			return this;
		}
		return this.loadDetail(id);
	}

	clearErrorMessages() {
		return new EntityDetailModel(this.#data.deleteIn(errorMessageListPath));
	}

	setCreateReadOnlyMap(func) {
		return new EntityDetailModel(this.#data.set('createReadOnlyMap', func));
	}

	setProperty(path, value) {
		return new EntityDetailModel(setProperty(this.#data, path, value));
	}

	resetVolatile() {
		return new EntityDetailModel(this.#data.deleteIn(getTabularInputIndexPathBase()).set('moduleKey', moduleKeySequence++));
	}

	setCenterToolbar(func) {
		return new EntityDetailModel(this.#data.set('centerToolbar', func));
	}

	async resetToOriginal() {
		let model = new EntityDetailModel(this.#data.set('detail', this.#data.get('originalDetail'))).resetVolatile();
		model = await model.validateAll({
			storeOriginalErrors: true
		});

		return model;
	}

	async validateAll(options) {
		options = options || {};
		const displayErrorMessages = options.displayErrorMessages;
		const ignoreIgnorableErrors = options.ignoreIgnorableErrors;
		const storeOriginalErrors = options.storeOriginalErrors;
		const data = this.#data || EMPTY_MAP;
		const definition = data.get('definition');
		if (!definition) {
			return null;
		}
		const ctx = new RenderContext(new Binder({
			getData: () => data
		}, this)).withCalculatedProperties(data.get('calculateContextProperties'));
		const errors = validateAll(ctx, definition);
		if (definition.validateAsync && options.validateAll) {
			await definition.validateAsync(ctx, errors);
		}
		const errorEntries = errors.getErrorEntries();
		if (displayErrorMessages) {
			if (errorEntries.size > 0) {
				const originalErrors = this.#data.getIn(originalErrorPath) || EMPTY_MAP;
				let warnings = [];
				let errors = [];
				let unmodifiedErrors = [];
				errorEntries.forEach(ee => {
					const eeKey = getErrorEntryKey(ee);
					const error = ee.get('error');
					const type = error.get('type');
					const code = error.get('code');
					const message = error.get('longMessage') || error.get('message');
					if (type === 'warning') {
						if (warnings.indexOf(message) < 0) {
							warnings.push(message);
						}
					} else if (originalErrors.get(eeKey)) {
						if (unmodifiedErrors.indexOf(message)) {
							unmodifiedErrors.push(message);
						}
					} else {
						if (errors.indexOf(message) < 0) {
							errors.push(message);
						}
					}
				});
				if (ignoreIgnorableErrors && errors.length === 0) {
					this.#data = this.#data.deleteIn(errorMessageListPath);
				} else {
					this.#data = this.#data.setIn(errorMessageListPath, Immutable.fromJS({
						errors,
						unmodifiedErrors,
						warnings
					}));
				}
			}
		}
		if (storeOriginalErrors) {
			let originalErrors = {};
			errorEntries.forEach(ee => {
				originalErrors[getErrorEntryKey(ee)] = true;
			})
			this.#data = this.#data.setIn(originalErrorPath, Immutable.Map(originalErrors));
		}

		return this.setErrors(errors.toMap());
	}

	setErrors(errors) {
		return new EntityDetailModel(this.#data.setIn(errorPath, errors));
	}

	hasErrors() {
		return this.getErrors().size > 0;
	}

	getErrors() {
		return this.#data.getIn(errorPath) || EMPTY_MAP;
	}

	setMessage(msg) {
		return new EntityDetailModel(this.#data.set('_message', msg));
	}

	static getMessage(data) {
		if (!data) {
			return null;
		}
		return data.get('_message');
	}

	clearMessage() {
		return this.setMessage(null);
	}

	async saveDetail(ignoreIgnorableErrors) {
		// zpracovani detailu pro ulozeni
		const processForSave = this.#data.get('processForSave');
		let detail = this.#data.get('detail');
		if (processForSave) {
			detail = processForSave(detail, this.#data.get('originalDetail'));
		}
		// validace
		this.#data = (await this.validateAll({
			displayErrorMessages: true,
			ignoreIgnorableErrors,
			validateAll: true
		})).getData();
		const hasUnignorableErrors = (this.#data.getIn(errorMessageListPath.concat('errors')) || EMPTY_LIST).size > 0;
		if ((this.hasErrors() && !ignoreIgnorableErrors) || hasUnignorableErrors) {
			return this;
		}

		// ulozeni
		const saveRes = await webapi.execute(this.#data.get('saveUri'), detail.toJS());
		//console.log('saveRes = ', saveRes, REPORTED_EXCEPTION, saveRes === REPORTED_EXCEPTION);
		if (saveRes === REPORTED_EXCEPTION) {
			return this;
		}
		// nacteni ulozeneho detailu
		const resType = typeof saveRes;
		const id = resType === 'number' || resType === 'string' ? saveRes : saveRes.id;
		const _message = resType === 'object' ? saveRes._message : null;
		const res = await this.loadDetail(id, {
			_message
		});
		// aktualizovat timestamp pro entity
		const entityName = res.getEntityName();
		if (entityName) {
			updateEntityTimestamp(entityName);
		}
		return res.resetVolatile();
	}

	setDefinition(definition) {
		return new EntityDetailModel(this.#data.set('definition', definition));
	}

	setCalculateContextProperties(ccp) {
		return new EntityDetailModel(this.#data.set('calculateContextProperties', ccp));
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}
